/**
 * Change Tracking UI Test Page
 * Prototype for visual change tracking (gutter markers + AI edit widgets)
 *
 * Access: http://localhost:5173/?test=change-tracking
 */

import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Check, Copy, MoreHorizontal, ChevronDown, Play, Loader2, ArrowLeft } from 'lucide-react';
import './test-change-tracking.css';
import { triggerProposedEdit, createAddLineTestEdit, createModifyTestEdit, type TestEditData } from './test-edit-trigger';
import { DiffPreviewDialog } from './layout/diff-preview-dialog';
import { MarkdownEditor } from './features/markdown-editor';
import { diffLines } from 'diff';
import type { ChangedLinesState } from '@/lib/codemirror-extensions/change-gutter';

export function ChangeTrackingTest() {
    const [activeTab, setActiveTab] = useState<'gutter' | 'widget' | 'buttons' | 'full' | 'trigger'>('gutter');
    const [showWidget, setShowWidget] = useState(true);

    const tabs = [
        { id: 'gutter', label: 'Gutter Markers' },
        { id: 'widget', label: 'File Edit Widget' },
        { id: 'buttons', label: 'Undo/Keep Buttons' },
        { id: 'full', label: 'Full Flow' },
        { id: 'trigger', label: 'Programmatic Triggers' },
    ];

    return (
        <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b p-4 bg-sidebar">
        <div className="flex items-center gap-3 mb-2">
          <a
            href="/?test=index"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Prototype Index</span>
          </a>
        </div>
        <h1 className="text-xl font-bold mb-1">Change Tracking UI Prototype</h1>
        <p className="text-sm text-muted-foreground mb-1">
          Testing visual change indicators (Cursor-inspired)
        </p>
        <p className="text-xs text-muted-foreground mb-3">
          <strong>Key:</strong> 🟢 Green bars = lines added | 🔴 Red chevrons = lines removed (shown between lines)
        </p>

                {/* Tab Switcher */}
                <div className="flex gap-2">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${activeTab === tab.id
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted hover:bg-muted/80'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
                {activeTab === 'gutter' && <GutterMarkersTest />}
                {activeTab === 'widget' && <FileEditWidgetTest showWidget={showWidget} setShowWidget={setShowWidget} />}
                {activeTab === 'buttons' && <UndoKeepButtonsTest />}
                {activeTab === 'full' && <FullFlowTest />}
                {activeTab === 'trigger' && <ProgrammaticTriggersTest />}
            </div>
        </div>
    );
}

// Tab 1: Gutter Markers Test
function GutterMarkersTest() {
  return (
    <div className="max-w-4xl">
      <h2 className="text-lg font-semibold mb-4">Gutter Markers (CodeMirror-style)</h2>

      <p className="text-sm text-muted-foreground mb-4">
        <strong>Green bars</strong> show added lines. <strong>Red chevrons</strong> appear between lines where content was removed (deleted lines don't show in the editor).
      </p>

      <div className="space-y-6">
                {/* Example 1: Green Added Lines */}
                <div>
                    <h3 className="text-sm font-medium mb-2">Green = Added Lines</h3>
                    <div className="border rounded-lg overflow-hidden bg-card">
                        <div className="flex font-mono text-sm">
                            {/* Gutter */}
                            <div className="bg-muted/50 select-none">
                                <div className="flex items-center h-6 px-2 text-muted-foreground">1</div>
                                <div className="flex items-center h-6 px-2 text-muted-foreground">2</div>
                                <div className="flex items-center h-6 px-2 text-muted-foreground">3</div>
                                <div className="flex items-center h-6 px-2 text-muted-foreground relative">
                                    4
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500/80" title="Line added"></div>
                                </div>
                                <div className="flex items-center h-6 px-2 text-muted-foreground relative">
                                    5
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500/80" title="Line added"></div>
                                </div>
                                <div className="flex items-center h-6 px-2 text-muted-foreground relative">
                                    6
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500/80" title="Line added"></div>
                                </div>
                                <div className="flex items-center h-6 px-2 text-muted-foreground">7</div>
                            </div>

                            {/* Code */}
                            <div className="flex-1 px-4 py-1">
                                <div className="h-6 flex items-center"># GGIA Work Spec</div>
                                <div className="h-6 flex items-center"></div>
                                <div className="h-6 flex items-center">## Dark Mode</div>
                                <div className="h-6 flex items-center bg-green-500/10">const config = &#123;</div>
                                <div className="h-6 flex items-center bg-green-500/10">  darkMode: 'class',</div>
                                <div className="h-6 flex items-center bg-green-500/10">&#125;;</div>
                                <div className="h-6 flex items-center"></div>
                            </div>
                        </div>
                    </div>
                </div>

        {/* Example 2: Red Removed Line */}
        <div>
          <h3 className="text-sm font-medium mb-2">Red Chevron = Lines Removed (Between Lines)</h3>
          <div className="border rounded-lg overflow-hidden bg-card">
            <div className="flex font-mono text-sm">
              {/* Gutter */}
              <div className="bg-muted/50 select-none">
                <div className="flex items-center h-6 px-2 text-muted-foreground">8</div>
                <div className="flex items-center h-6 px-2 text-muted-foreground">9</div>
                {/* Red chevron BETWEEN lines (where content was removed) */}
                <div className="flex items-center h-3 px-2 relative border-t border-red-500/30">
                  <div className="absolute left-0 top-0 bottom-0 w-full flex items-center justify-center text-red-500 font-bold text-xs" title="3 lines removed here">
                    ▸
                  </div>
                </div>
                <div className="flex items-center h-6 px-2 text-muted-foreground">10</div>
                <div className="flex items-center h-6 px-2 text-muted-foreground">11</div>
              </div>

              {/* Code */}
              <div className="flex-1 px-4 py-1">
                <div className="h-6 flex items-center">export default config;</div>
                <div className="h-6 flex items-center"></div>
                {/* Red indicator line where content was removed */}
                <div className="h-3 flex items-center border-t border-red-500/30 bg-red-500/5">
                  <span className="text-xs text-red-500/60 italic">3 lines removed</span>
                </div>
                <div className="h-6 flex items-center"></div>
                <div className="h-6 flex items-center">## Tasks</div>
              </div>
            </div>
          </div>
        </div>

                {/* Example 3: Mixed Changes */}
                <div>
                    <h3 className="text-sm font-medium mb-2">Mixed: Added + Removed</h3>
                    <div className="border rounded-lg overflow-hidden bg-card">
                        <div className="flex font-mono text-sm">
                            {/* Gutter */}
                            <div className="bg-muted/50 select-none">
                                <div className="flex items-center h-6 px-2 text-muted-foreground">1</div>
                                <div className="flex items-center h-6 px-2 text-muted-foreground relative">
                                    2
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500/80"></div>
                                </div>
                                <div className="flex items-center h-6 px-2 text-muted-foreground relative">
                                    3
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500/80"></div>
                                </div>
                                {/* Red chevron BETWEEN lines */}
                                <div className="flex items-center h-3 px-2 relative border-t border-red-500/30">
                                    <div className="absolute left-0 top-0 bottom-0 w-full flex items-center justify-center text-red-500 font-bold text-xs" title="2 lines removed here">
                                        ▸
                                    </div>
                                </div>
                                <div className="flex items-center h-6 px-2 text-muted-foreground">4</div>
                            </div>

                            {/* Code */}
                            <div className="flex-1 px-4 py-1">
                                <div className="h-6 flex items-center"># Document</div>
                                <div className="h-6 flex items-center bg-green-500/10">New line added</div>
                                <div className="h-6 flex items-center bg-green-500/10">Another new line</div>
                                {/* Indicator where content was removed (between lines) */}
                                <div className="h-3 flex items-center border-t border-red-500/30 bg-red-500/5">
                                    <span className="text-xs text-red-500/60 italic">2 lines removed</span>
                                </div>
                                <div className="h-6 flex items-center">Unchanged line</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Tab 2: File Edit Widget Test
function FileEditWidgetTest({ showWidget, setShowWidget }: { showWidget: boolean, setShowWidget: (show: boolean) => void }) {
    return (
        <div className="max-w-2xl">
            <h2 className="text-lg font-semibold mb-4">File Edit Widget (Cursor-style)</h2>

            <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                    This widget appears in chat after AI proposes changes
                </p>

                {showWidget ? (
                    <div className="border border-sidebar-border rounded-lg p-3 bg-sidebar">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <ChevronDown className="w-4 h-4 cursor-pointer" />
                                <span className="text-sm font-medium">1 File Edited</span>
                                <button className="text-blue-500 text-sm hover:underline">
                                    Review
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    className="text-muted-foreground hover:text-red-500 transition-colors"
                                    title="Reject changes"
                                    onClick={() => {
                                        console.log('Reject clicked');
                                        alert('Changes rejected!');
                                        setShowWidget(false);
                                    }}
                                >
                                    <X className="w-4 h-4" />
                                </button>
                                <button
                                    className="text-muted-foreground hover:text-green-500 transition-colors"
                                    title="Accept changes"
                                    onClick={() => {
                                        console.log('Accept clicked');
                                        alert('Changes accepted! (Gutter markers would now appear)');
                                        setShowWidget(false);
                                    }}
                                >
                                    <Check className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* File Details */}
                        <div className="flex items-center justify-between pl-6">
                            <div className="flex items-center gap-2">
                                <ChevronDown className="w-4 h-4 text-blue-500" />
                                <span className="text-sm text-foreground">GGIA.md</span>
                                <span className="text-sm text-green-500 font-medium">+42</span>
                                <span className="text-sm text-red-500 font-medium">-5</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button className="text-muted-foreground hover:text-foreground">
                                    <Copy className="w-4 h-4" />
                                </button>
                                <button className="text-muted-foreground hover:text-foreground">
                                    <MoreHorizontal className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="border-2 border-dashed rounded-lg p-8 text-center">
                        <p className="text-sm text-muted-foreground mb-4">
                            Widget hidden (user accepted or rejected)
                        </p>
                        <Button onClick={() => setShowWidget(true)} variant="outline" size="sm">
                            Show Widget Again
                        </Button>
                    </div>
                )}

                {/* Variations */}
                <div className="space-y-3 mt-8">
                    <h3 className="text-sm font-medium">Variations:</h3>

                    {/* Multiple files */}
                    <div className="border border-sidebar-border rounded-lg p-3 bg-sidebar">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <ChevronDown className="w-4 h-4" />
                                <span className="text-sm font-medium">3 Files Edited</span>
                                <button className="text-blue-500 text-sm hover:underline">
                                    Review
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                <button className="text-muted-foreground hover:text-red-500">
                                    <X className="w-4 h-4" />
                                </button>
                                <button className="text-muted-foreground hover:text-green-500">
                                    <Check className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="space-y-1 pl-6 text-sm">
                            <div className="flex items-center justify-between">
                                <span>GGIA.md</span>
                                <div className="flex gap-2">
                                    <span className="text-green-500">+42</span>
                                    <span className="text-red-500">-5</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span>config.ts</span>
                                <div className="flex gap-2">
                                    <span className="text-green-500">+12</span>
                                    <span className="text-red-500">-2</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span>README.md</span>
                                <div className="flex gap-2">
                                    <span className="text-green-500">+8</span>
                                    <span className="text-red-500">-0</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Tab 3: Undo/Keep Buttons Test
function UndoKeepButtonsTest() {
    const [status, setStatus] = useState<'pending' | 'accepted' | 'rejected'>('pending');

    return (
        <div className="max-w-2xl">
            <h2 className="text-lg font-semibold mb-4">Undo/Keep Buttons (Cursor-style)</h2>

            <div className="space-y-6">
                <p className="text-sm text-muted-foreground">
                    Primary action buttons for accepting or rejecting AI edits
                </p>

                {/* Status Indicator */}
                <div className={`p-3 rounded-lg border ${status === 'accepted' ? 'bg-green-500/10 border-green-500/20' :
                        status === 'rejected' ? 'bg-red-500/10 border-red-500/20' :
                            'bg-blue-500/10 border-blue-500/20'
                    }`}>
                    <p className="text-sm font-medium">
                        Status: {status === 'pending' ? '⏳ Awaiting your decision' :
                            status === 'accepted' ? '✅ Changes accepted' :
                                '❌ Changes rejected'}
                    </p>
                </div>

        {/* Button Group */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              console.log('Undo clicked');
              setStatus('rejected');
              setTimeout(() => setStatus('pending'), 2000);
            }}
            disabled={status !== 'pending'}
            className="flex items-center gap-2"
          >
            Undo
            <kbd className="pointer-events-none inline-flex h-4 select-none items-center gap-0.5 rounded border bg-muted px-1 font-mono text-[9px] font-medium text-muted-foreground opacity-100">
              <span className="text-[10px]">⌘</span>N
            </kbd>
          </Button>

          <Button
            size="sm"
            onClick={() => {
              console.log('Keep clicked');
              setStatus('accepted');
              setTimeout(() => setStatus('pending'), 2000);
            }}
            disabled={status !== 'pending'}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          >
            Keep
            <kbd className="pointer-events-none inline-flex h-4 select-none items-center gap-0.5 rounded border bg-green-700 px-1 font-mono text-[9px] font-medium text-white opacity-100">
              <span className="text-[10px]">⌘</span>Y
            </kbd>
          </Button>
        </div>

                {/* Keyboard Shortcut Test */}
                <div className="border rounded-lg p-4 bg-muted/50">
                    <h3 className="text-sm font-medium mb-2">Try keyboard shortcuts:</h3>
                    <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Press <kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">⌘N</kbd> to undo (reject)</li>
                        <li>• Press <kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">⌘Y</kbd> to keep (accept)</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-3">
                        Note: Keyboard shortcuts not implemented in prototype (console.log only)
                    </p>
                </div>

        {/* Color Variations */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Button Style Variations:</h3>

          {/* Small (recommended) */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm">Undo ⌘N</Button>
            <Button size="sm" className="bg-green-600 hover:bg-green-700">Keep ⌘Y</Button>
          </div>

          {/* Small with icons */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5">
              <X className="w-3 h-3" />
              Undo ⌘N
            </Button>
            <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1.5">
              <Check className="w-3 h-3" />
              Keep ⌘Y
            </Button>
          </div>

          {/* Default (medium) */}
          <div className="flex gap-3">
            <Button variant="outline">Undo ⌘N</Button>
            <Button className="bg-green-600 hover:bg-green-700">Keep ⌘Y</Button>
          </div>
        </div>
            </div>
        </div>
    );
}

// Tab 4: Full Flow Test
function FullFlowTest() {
    const [step, setStep] = useState<'proposal' | 'reviewing' | 'accepted' | 'rejected'>('proposal');

    return (
        <div className="max-w-4xl">
            <h2 className="text-lg font-semibold mb-4">Full AI Edit Flow</h2>

            <div className="space-y-4">
                {/* Step Indicator */}
                <div className="flex items-center gap-4 text-sm">
                    <div className={`px-3 py-1.5 rounded ${step === 'proposal' ? 'bg-blue-500/20 text-blue-500' : 'bg-muted text-muted-foreground'}`}>
                        1. Proposal
                    </div>
                    <div className="h-px flex-1 bg-border"></div>
                    <div className={`px-3 py-1.5 rounded ${step === 'reviewing' ? 'bg-blue-500/20 text-blue-500' : 'bg-muted text-muted-foreground'}`}>
                        2. Review
                    </div>
                    <div className="h-px flex-1 bg-border"></div>
                    <div className={`px-3 py-1.5 rounded ${(step === 'accepted' || step === 'rejected') ? 'bg-blue-500/20 text-blue-500' : 'bg-muted text-muted-foreground'}`}>
                        3. Decision
                    </div>
                </div>

                {/* Content */}
                {step === 'proposal' && (
                    <div className="border rounded-lg p-4 space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                                AI
                            </div>
                            <div className="flex-1">
                                <p className="text-sm mb-3">
                                    I've updated the dark mode implementation as requested:
                                </p>
                                <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                                    <li>• Added Tailwind dark mode configuration</li>
                                    <li>• Updated component styles for dark theme</li>
                                    <li>• Fixed color variable definitions</li>
                                </ul>

                                {/* File Edit Widget */}
                                <div className="border border-sidebar-border rounded-lg p-3 bg-sidebar mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <ChevronDown className="w-4 h-4" />
                                            <span className="text-sm font-medium">1 File Edited</span>
                                            <button
                                                className="text-blue-500 text-sm hover:underline"
                                                onClick={() => setStep('reviewing')}
                                            >
                                                Review
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                className="text-muted-foreground hover:text-red-500"
                                                onClick={() => setStep('rejected')}
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                            <button
                                                className="text-muted-foreground hover:text-green-500"
                                                onClick={() => setStep('accepted')}
                                            >
                                                <Check className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 pl-6 text-sm">
                                        <ChevronDown className="w-4 h-4 text-blue-500" />
                                        <span>GGIA.md</span>
                                        <span className="text-green-500">+42</span>
                                        <span className="text-red-500">-5</span>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setStep('rejected')}
                                    >
                                        Undo ⌘N
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="bg-green-600 hover:bg-green-700"
                                        onClick={() => setStep('accepted')}
                                    >
                                        Keep ⌘Y
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {step === 'reviewing' && (
                    <div className="border rounded-lg p-4">
                        <div className="text-center text-muted-foreground py-8">
                            <p className="mb-4">Diff Preview Dialog would open here</p>
                            <p className="text-sm mb-6">(Already implemented in FlowNotes)</p>
                            <div className="flex gap-2 justify-center">
                                <Button variant="outline" size="sm" onClick={() => setStep('proposal')}>
                                    Back
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setStep('rejected')}>
                                    Reject
                                </Button>
                                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setStep('accepted')}>
                                    Accept
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {step === 'accepted' && (
                    <div className="border border-green-500/20 rounded-lg p-4 bg-green-500/5">
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                                ✓
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-green-600 mb-2">Changes accepted!</p>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Gutter markers will now appear showing added/removed lines.
                                </p>

                                {/* Mock Editor with Gutter Markers */}
                                <div className="border rounded-lg overflow-hidden bg-card">
                                    <div className="flex font-mono text-xs">
                                        <div className="bg-muted/50 select-none">
                                            <div className="flex items-center h-5 px-2 text-muted-foreground">5</div>
                                            <div className="flex items-center h-5 px-2 text-muted-foreground relative">
                                                6<div className="absolute left-0 top-0 bottom-0 w-0.5 bg-green-500/80"></div>
                                            </div>
                                            <div className="flex items-center h-5 px-2 text-muted-foreground relative">
                                                7<div className="absolute left-0 top-0 bottom-0 w-0.5 bg-green-500/80"></div>
                                            </div>
                                            <div className="flex items-center h-5 px-2 text-muted-foreground">8</div>
                                        </div>
                                        <div className="flex-1 px-3 py-0.5 text-xs">
                                            <div className="h-5 flex items-center">## Dark Mode</div>
                                            <div className="h-5 flex items-center bg-green-500/10">const config = &#123; darkMode: 'class' &#125;;</div>
                                            <div className="h-5 flex items-center bg-green-500/10">export default config;</div>
                                            <div className="h-5 flex items-center">## Tasks</div>
                                        </div>
                                    </div>
                                </div>

                                <Button variant="outline" size="sm" className="mt-4" onClick={() => setStep('proposal')}>
                                    Start Over
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {step === 'rejected' && (
                    <div className="border border-red-500/20 rounded-lg p-4 bg-red-500/5">
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
                                ✕
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-red-600 mb-2">Changes rejected</p>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Document unchanged. No gutter markers will appear.
                                </p>
                                <Button variant="outline" size="sm" onClick={() => setStep('proposal')}>
                                    Start Over
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// Tab 5: Programmatic Triggers Test
function ProgrammaticTriggersTest() {
    const [documentId, setDocumentId] = useState('hop-studios/current-work-priorities.md');
    const [originalContent, setOriginalContent] = useState('');
    const [proposedContent, setProposedContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [lastResult, setLastResult] = useState<string | null>(null);
    const [proposedEdit, setProposedEdit] = useState<{
        documentId: string;
        filename: string;
        original: string;
        proposed: string;
        frontmatter?: any;
        explanation?: string;
    } | null>(null);
    const [diffPreviewOpen, setDiffPreviewOpen] = useState(false);
    const [isApplying, setIsApplying] = useState(false);

    // Debounce diff computation to prevent jumping/cursor loss during typing
    const [debouncedProposedContent, setDebouncedProposedContent] = useState(proposedContent);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedProposedContent(proposedContent);
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [proposedContent]);

    // Compute changed lines for the proposed content (to show in CodeMirror)
    // Line numbers must be relative to the PROPOSED content (what's shown in the editor)
    const proposedChangedLines = useMemo<ChangedLinesState | null>(() => {
        if (!originalContent || !debouncedProposedContent) return null;

        try {
            const changes = diffLines(originalContent, debouncedProposedContent);
            const added: number[] = [];
            const removed: number[] = [];

            // Track line numbers in the PROPOSED content (what CodeMirror shows)
            let proposedLineNum = 1;

            changes.forEach(change => {
                const lines = change.value.split('\n');
                const lineCount = change.value.endsWith('\n') ? lines.length - 1 : lines.length;

                if (change.added) {
                    // Added lines appear in the PROPOSED content at current position
                    for (let i = 0; i < lineCount; i++) {
                        if (lines[i] && lines[i].trim().length > 0) {
                            const lineNumber = proposedLineNum + i;
                            if (lineNumber >= 1) {
                                added.push(lineNumber);
                            }
                        }
                    }
                    // Advance proposed line number for added lines
                    proposedLineNum += lineCount;
                } else if (change.removed) {
                    // Removed lines: mark the position in PROPOSED content where they would have been
                    // (they don't exist in proposed, but we mark the position for visual reference)
                    for (let i = 0; i < lineCount; i++) {
                        if (lines[i] && lines[i].trim().length > 0) {
                            // Removed lines are shown between lines, so use the current position
                            const lineNumber = proposedLineNum;
                            if (lineNumber >= 1) {
                                removed.push(lineNumber);
                            }
                        }
                    }
                    // Don't advance proposed line number for removed lines (they don't exist in proposed)
                } else {
                    // Unchanged lines: advance both counters
                    proposedLineNum += lineCount;
                }
            });

            // CodeMirror requires ranges to be sorted by position and deduplicated
            // Remove duplicates and sort arrays in ascending order
            const uniqueAdded = Array.from(new Set(added)).sort((a, b) => a - b);
            const uniqueRemoved = Array.from(new Set(removed)).sort((a, b) => a - b);

            return { added: uniqueAdded, removed: uniqueRemoved, modified: [] };
        } catch (error) {
            console.error('[ProgrammaticTriggersTest] Error computing changed lines:', error);
            return null;
        }
    }, [originalContent, debouncedProposedContent]);

    // Listen for test-proposed-edit events
    useEffect(() => {
        const handleTestProposedEdit = (event: CustomEvent) => {
            const editData = event.detail;
            console.log('[ProgrammaticTriggersTest] Received test proposed edit:', editData);

            if (editData.type === 'proposed_edit') {
                setProposedEdit({
                    documentId: editData.documentId,
                    filename: editData.filename || 'document.md',
                    original: editData.original || '',
                    proposed: editData.proposed || '',
                    frontmatter: editData.frontmatter,
                    explanation: editData.explanation
                });
                setDiffPreviewOpen(true);
                setLastResult('✅ Edit triggered! Diff preview opened.');
            }
        };

        window.addEventListener('test-proposed-edit', handleTestProposedEdit as unknown as EventListener);

        return () => {
            window.removeEventListener('test-proposed-edit', handleTestProposedEdit as unknown as EventListener);
        };
    }, []);

    // Handle accept/reject actions
    const handleAcceptEdit = async () => {
        if (!proposedEdit) return;

        setIsApplying(true);
        try {
            // In a real scenario, this would call the API to apply the edit
            // For testing, we'll just show a success message
            setLastResult('✅ Edit accepted! (This is a test - edit not actually applied)');
            setDiffPreviewOpen(false);
            setProposedEdit(null);
        } catch (error) {
            setLastResult(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsApplying(false);
        }
    };

    const handleRejectEdit = async () => {
        setLastResult('❌ Edit rejected.');
        setDiffPreviewOpen(false);
        setProposedEdit(null);
    };

    // Fetch current document content
    const fetchDocument = async () => {
        try {
            setIsLoading(true);
            const path = documentId.includes('/') ? documentId : undefined;
            const { fetchWithAuth: fetchDoc } = await import('@/lib/api');
            const url = path
                ? `/api/documents/by-path?path=${encodeURIComponent(path)}`
                : `/api/documents/${documentId}`;

            const response = await fetchDoc(url);
            if (response.ok) {
                const doc = await response.json();
                setOriginalContent(doc.content || '');
                setProposedContent(doc.content || '');
                setLastResult(`✅ Loaded document: ${doc.title || doc.file_path}`);
            } else {
                setLastResult(`❌ Failed to load document: ${response.status}`);
            }
        } catch (error) {
            setLastResult(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTriggerEdit = async (editData: TestEditData) => {
        try {
            setIsLoading(true);
            setLastResult(null);
            const success = await triggerProposedEdit(editData);
            if (success) {
                // The diff preview will open via the event listener
                // Don't set lastResult here - let the event listener handle it
            } else {
                setLastResult('❌ Failed to trigger edit');
            }
        } catch (error) {
            setLastResult(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickAdd = () => {
        if (!originalContent) {
            setLastResult('❌ Please load document content first');
            return;
        }
        const edit = createAddLineTestEdit(
            documentId,
            originalContent,
            `Test line added at ${new Date().toLocaleTimeString()}`,
            documentId.includes('/') ? documentId.split('/').pop() : 'document.md'
        );
        handleTriggerEdit(edit);
    };

    const handleQuickModify = () => {
        if (!originalContent) {
            setLastResult('❌ Please load document content first');
            return;
        }
        // Find first line with content and modify it
        const lines = originalContent.split('\n');
        const firstContentLine = lines.findIndex(line => line.trim().length > 0);
        if (firstContentLine === -1) {
            setLastResult('❌ No content lines found to modify');
            return;
        }
        const originalLine = lines[firstContentLine];
        const modifiedLine = originalLine + ' [MODIFIED]';
        const edit = createModifyTestEdit(
            documentId,
            originalContent,
            originalLine,
            modifiedLine,
            documentId.includes('/') ? documentId.split('/').pop() : 'document.md'
        );
        handleTriggerEdit(edit);
    };

    const handleCustomEdit = () => {
        if (!originalContent || !proposedContent) {
            setLastResult('❌ Both original and proposed content are required');
            return;
        }
        const edit: TestEditData = {
            documentId,
            original: originalContent,
            proposed: proposedContent,
            filename: documentId.includes('/') ? documentId.split('/').pop() : 'document.md',
            title: 'Test Document',
            explanation: 'Custom test edit triggered programmatically'
        };
        handleTriggerEdit(edit);
    };

    return (
        <div className="max-w-4xl space-y-6">
            <h2 className="text-lg font-semibold mb-4">Programmatic Edit Triggers</h2>

            <p className="text-sm text-muted-foreground mb-4">
                This page allows you to programmatically trigger proposed edits for testing purposes.
                Edits will appear in the chat pane and open the diff preview modal automatically.
            </p>

            {/* Document Selection */}
            <div className="border rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-medium">Document Selection</h3>
                <div className="flex gap-2">
                    <Input
                        value={documentId}
                        onChange={(e) => setDocumentId(e.target.value)}
                        placeholder="Document ID or path (e.g., hop-studios/current-work-priorities.md)"
                        className="flex-1"
                    />
                    <Button onClick={fetchDocument} disabled={isLoading}>
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load'}
                    </Button>
                </div>
                {lastResult && (
                    <p className={`text-sm ${lastResult.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>
                        {lastResult}
                    </p>
                )}
            </div>

            {/* Quick Actions */}
            <div className="border rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-medium">Quick Test Actions</h3>
                <div className="flex gap-2 flex-wrap">
                    <Button onClick={handleQuickAdd} disabled={isLoading || !originalContent} variant="outline">
                        <Play className="w-4 h-4 mr-2" />
                        Add Test Line
                    </Button>
                    <Button onClick={handleQuickModify} disabled={isLoading || !originalContent} variant="outline">
                        <Play className="w-4 h-4 mr-2" />
                        Modify First Line
                    </Button>
                </div>
            </div>

            {/* Custom Edit */}
            <div className="border rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-medium">Custom Edit</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-xs font-medium">
                            Original Content
                            {!originalContent && (
                                <span className="ml-2 text-xs text-muted-foreground">(Click "Load" to fetch document)</span>
                            )}
                        </label>
                        <div className="border rounded-lg overflow-hidden" style={{ height: '400px' }}>
                            {originalContent || proposedContent ? (
                                <MarkdownEditor
                                    key="original-editor" // Stable key
                                    initialContent={originalContent}
                                    onChange={(content) => setOriginalContent(content)}
                                    readOnly={true} // Original is just for reference - make it read-only
                                    viewMode="source"
                                    className="h-full"
                                    changedLines={null}
                                    showChangeMarkers={false}
                                />
                            ) : (
                                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                                    Click "Load" above to fetch document content
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium">
                            Proposed Content (with diff markers)
                            {!proposedContent && (
                                <span className="ml-2 text-xs text-muted-foreground">(Edit to see changes)</span>
                            )}
                        </label>
                        <div className="border rounded-lg overflow-hidden" style={{ height: '400px' }}>
                            {originalContent || proposedContent ? (
                                <MarkdownEditor
                                    key="proposed-editor" // Stable key - don't remount on content change
                                    initialContent={proposedContent}
                                    onChange={(content) => setProposedContent(content)}
                                    readOnly={false}
                                    viewMode="source"
                                    className="h-full"
                                    changedLines={proposedChangedLines}
                                    showChangeMarkers={proposedChangedLines !== null && (proposedChangedLines.added.length > 0 || proposedChangedLines.removed.length > 0)}
                                />
                            ) : (
                                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                                    Click "Load" above to fetch document content
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                        {proposedChangedLines && (
                            <span>
                                Changes: +{proposedChangedLines.added.length} -{proposedChangedLines.removed.length} lines
                            </span>
                        )}
                    </div>
                    <Button onClick={handleCustomEdit} disabled={isLoading || !originalContent || !proposedContent}>
                        <Play className="w-4 h-4 mr-2" />
                        Trigger Custom Edit
                    </Button>
                </div>
            </div>

            {/* Instructions */}
            <div className="border rounded-lg p-4 bg-muted/50">
                <h3 className="text-sm font-medium mb-2">How to Use</h3>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Enter a document ID or path (e.g., <code className="bg-background px-1 rounded">hop-studios/current-work-priorities.md</code>)</li>
                    <li>Click "Load" to fetch the current document content</li>
                    <li>Use quick actions or edit the content manually</li>
                    <li>Click a trigger button to programmatically trigger the edit</li>
                    <li>The diff preview modal should open automatically below</li>
                    <li>Accept or reject the edit to test the flow</li>
                </ol>
            </div>

            {/* Diff Preview Dialog */}
            {proposedEdit && (
                <DiffPreviewDialog
                    open={diffPreviewOpen}
                    onOpenChange={setDiffPreviewOpen}
                    original={proposedEdit.original}
                    proposed={proposedEdit.proposed}
                    filename={proposedEdit.filename}
                    onAccept={handleAcceptEdit}
                    onReject={handleRejectEdit}
                    isApplying={isApplying}
                />
            )}
        </div>
    );
}

